import { Injectable } from '@angular/core';
import { ApiRoutes } from '@sb/library/api';
import { Base, SnackBarDuration, SnackbarData, SnackbarEvents, SnackbarLayout, Storage, StorageBaseKeys, TemporaryReportDownload, TemporaryReportDownloadStatus, TemporaryReportDownloads } from '@sb/library/base';
import { DisplayItemCollection, ReportDisplayItem, ReportFormat, ReportInputItem, ReportSearchOptions, ReportStatus } from '@sb/library/data-models';
import { SnackbarService } from '@sb/library/snackbar';
import { Observable, Subject, take, takeUntil, timer } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class ReportDownloadManager extends Base {
    private _temporaryReports: TemporaryReportDownloads = {}

    private _polling: boolean = false;
    private _generatedReportObservables: Record<string, Subject<ReportDisplayItem>> = {};
    private _events: Subject<TemporaryReportDownloadStatus> = new Subject();
    private _eventsCompleted: Subject<void> = new Subject();

    private _reportReadySnackBarData: SnackbarData<string> = {
        Data: $localize `:@@CommonSnackbarReportIsReadyPrint:Your report is ready!`,
        ActionButtonText: $localize `:@@CommonOpenButton:Open`,
        IsClosable: true,
        Duration: SnackBarDuration.Important,
        Layout: SnackbarLayout.A,
    };

    constructor(private _SnackbarService: SnackbarService) {
        super();
        this._temporaryReports = Storage.GetValue(StorageBaseKeys.TemporaryReports, {});
        this.PollForTemporaryReports();
    }

    CreateReport(inputItem: ReportInputItem, readySnackbarData: SnackbarData<string> | null = null): Observable<TemporaryReportDownloadStatus> {
        if (readySnackbarData) {
            this._reportReadySnackBarData = readySnackbarData;
        }
        ApiRoutes.Reports.Add.Call({Body: inputItem}).subscribe(id => {
            this.WaitForTemporaryReport(id);
            this._events.next(TemporaryReportDownloadStatus.Processing);
        });
        this._eventsCompleted = new Subject();
        return this._events.pipe(takeUntil(this._eventsCompleted));
    }

    private WaitForTemporaryReport(id: string): Observable<ReportDisplayItem> {
        this._temporaryReports[id] = new TemporaryReportDownload({ Viewing: false });
        Storage.SetValue(StorageBaseKeys.TemporaryReports, this._temporaryReports);

        const subject = new Subject<ReportDisplayItem>();
        this._generatedReportObservables[id] = subject;

        if (!this._polling) {
            this.PollForTemporaryReports();
        }

        return subject.pipe(take(1)); // One-off observable (no need to unsubscribe)
    }

    private PollForTemporaryReports() {
        const idsToSearch = Object.keys(this._temporaryReports);
        this._polling = idsToSearch.length > 0;

        if (this._polling) {
            ApiRoutes.Reports.Search.Call({
                Body: new ReportSearchOptions({
                    IDs: idsToSearch
                })
            }).subscribe({
                next: reports => this.ProcessReport(reports),
                error: _ => {
                    timer(2500).subscribe(_ => this.PollForTemporaryReports())
                }
            });
        }
    }

    private ProcessReport(reports: DisplayItemCollection<ReportDisplayItem>) {
        reports.ItemSubset.forEach(report => {
            const reportID = report.ID!;

            if (report.Status != ReportStatus.Processing) {
                if (report.Status == ReportStatus.Completed) {
                    this._events.next(TemporaryReportDownloadStatus.Ready);
                    this._eventsCompleted.next();
                    this._eventsCompleted.complete();

                    if (report.Format != ReportFormat.PDF) {
                        this.StartDownload(report, false);
                    } else {
                        this._SnackbarService.CreateActionSnackBar(this._reportReadySnackBarData).events.subscribe((event: SnackbarEvents) => {
                            switch (event) {
                                case SnackbarEvents.OnAction:
                                    this.StartDownload(report, true);
                                    this._SnackbarService.DismissSnackbar();
                                    break;
                            }
                        });
                    }
                } else {  // Failed
                    // TODO: Handle error case more gracefully (for now, we are just removing the banner)
                    this._eventsCompleted.next();
                    this._eventsCompleted.complete();
                }

                if (this._generatedReportObservables[reportID]) {
                    this._generatedReportObservables[reportID].next(report);
                    delete this._generatedReportObservables[reportID];
                }

                delete this._temporaryReports[reportID];
                Storage.SetValue(StorageBaseKeys.TemporaryReports, this._temporaryReports);
            }
        });

        timer(2500).subscribe(_ => this.PollForTemporaryReports());
    }

    private StartDownload(report: ReportDisplayItem, view: boolean) {
        if (view) {
            window.open(report.ViewURL!, '_blank');
        } else {
            window.open(report.DownloadURL!, '_self');
        }
    }
}
